import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, IconButton, ListItemButton, Stack, Typography, CircularProgress } from '@mui/material';
import WidgetsIcon from '@mui/icons-material/Widgets';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useBundles } from '../../../hooks/inventory/useBundles';
import { useDeleteBundle } from '../../../hooks/inventory/useDeleteBundle';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getBundleById } from '../../../api/inventoryService';
import BundleItems from './BundleItems';
import BundleForm from '../forms/BundleForm';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function Bundles({mode = 'list', onCloseCallback = null}) {
    const { t } = useTranslation('common'); 
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        resetParent();
        if (onCloseCallback) onCloseCallback();
    };

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");

    const [selectedBundle, setSelectedBundle] = React.useState(null);

    const handleListItemClick = (item) => {
        setViewOpen(true);
        setSelectedBundle(item);
    };

    const { showSnackbar } = useSnackbar();

    const { bundles, loading, loadMore, searchFunction, resetParent } = useBundles(open, 1, 10);
    const { remove } = useDeleteBundle(resetParent);

    const handleAdd = () => {
        setSelectedBundle(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedBundle(item);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSaveSuccess = async (savedItem) => {
        await resetParent();
        const updatedItem = await getBundleById(savedItem.id);
        setSelectedBundle(updatedItem);
        if (formMode === 'create')
        {
          if (!viewOpen) setViewOpen(true);
        }
    };

    const onAddItemSuccess = async (savedItem) => {
        await resetParent();
        const updatedBundle = await getBundleById(savedItem[0].bundle_id);
        setSelectedBundle(updatedBundle);
    };

    const onSaveQuantitySuccess = async (savedItem) => {
        await resetParent();
        const updatedBundle = await getBundleById(savedItem.bundle_id);
        setSelectedBundle(updatedBundle);
    };

    const onRemoveItemSuccess = async (removedItem) => {
        await resetParent();
        const updatedBundle = await getBundleById(removedItem.bundle_id);
        setSelectedBundle(updatedBundle);
    };

    const handleDelete = async (item) => {
        try {
            await remove(item.id);
            showSnackbar(t('inventory.bundles.messages.delete_success', { name: item.name }), "success");
        } catch (err) {
            showSnackbar(t('inventory.bundles.messages.delete_fail', { name: item.name }), "error");
        }
    };

    return (
        <React.Fragment>
          {mode === 'list' ? 
          <ListItem>
              <ListItemButton onClick={handleClickOpen}>
              <ListItemIcon>
                  <WidgetsIcon fontSize='large' color='secondary'/>
              </ListItemIcon>
              <ListItemText 
                  primary="Item Bundles"
                  secondary="view and manage item bundles"
              />
              </ListItemButton>
          </ListItem>
          : null }
          {mode === 'button' ? 
          <Button onClick={handleClickOpen}>Manage Bundles</Button>
          : null }

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={2}>
                <Typography variant="h6">Manage Item Bundles</Typography>
                <SearchBox searchFunction={searchFunction} resetParent={resetParent} />
                <Divider />
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                {loading ? (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      minHeight: 500 
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                <List sx={{ minHeight: 500 }}>
                    {bundles?.data?.map((bundle) => (
                    <ListItem
                        key={bundle.id}
                        secondaryAction={
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                            <VerticalHamburgerMenu
                            options={[
                                { 
                                    id: 1, 
                                    name: 'View',
                                    callback: () => handleListItemClick(bundle),
                                    disabled: !userHasAccess(SUB_MODULES.ITEM_BUNDLES, 1)
                                },
                                { 
                                    id: 2, 
                                    name: 'Edit',
                                    callback: () => handleEdit(bundle),
                                    disabled: !userHasAccess(SUB_MODULES.ITEM_BUNDLES, 2)
                                },
                                {
                                    id: 4,
                                    name: 'Delete',
                                    callback: () => handleDelete(bundle),
                                    confirmMessage: t('inventory.bundles.messages.delete_confirm', { name: `${bundle.name}` }),
                                    disabled: !userHasAccess(SUB_MODULES.ITEM_BUNDLES, 3)
                                },
                            ]}
                            />
                        </Box>
                        }
                    >
                        <ListItemButton onClick={() => { if (userHasAccess(SUB_MODULES.ITEM_BUNDLES, 1)) handleListItemClick(bundle)}}>
                            <ListItemIcon>
                                <WidgetsIcon fontSize='large' color='primary' />
                            </ListItemIcon>
                            <ListItemText
                            primary={`${bundle.name}`}
                            secondary={ 
                            <>
                                <Typography component="span" variant="body2" >
                                {`${bundle.items.length} item(s)`}
                                </Typography>
                            </>
                            }
                            />
                        </ListItemButton>
                    </ListItem>
                    ))}
                    {bundles.length === 0 ?
                    <ListItem sx={{ justifyContent: "center" }}>
                      <Typography component="span" variant="body2" color="text.primary">
                        no bundles
                      </Typography>
                    </ListItem>
                    : null }
                </List>
                )}
                {/* Load More only shows if more pages exist */}
                {bundles?.pagination?.page < bundles?.pagination?.totalPages && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <IconButton 
                            onClick={loadMore}
                            sx={{
                                width: '100%',              // fill container width
                                justifyContent: 'center',   // center the icon horizontally
                                color: 'primary.main',      // use theme's primary color
                            }}
                        >
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Box>
                )}
                </>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.ITEM_BUNDLES} minAccessLevel={2}>
                <Button onClick={handleAdd}>Add Bundle</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <BundleItems 
            loading={loading}
            onAddItemSuccess={onAddItemSuccess}
            onSaveQuantitySuccess={onSaveQuantitySuccess}
            onRemoveItemSuccess={onRemoveItemSuccess}
            bundle={selectedBundle}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <BundleForm
            successCallback={onSaveSuccess}
            bundle={selectedBundle}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        
        </React.Fragment>
    );
}